import { LocalizedPercentage, LocalizedText, useLocalizeText } from '@mews/framework';
import { FlatChartCard } from '@mews/b2b-ui';
import { TextKey } from '@mews-commander/enums';
import { DisplayBillState, InvoiceStatusDistribution } from '@mews-commander/data-access';
import { LocalizedDateFromUtc } from '@mews-commander/ui';
import { Typography } from '@optimus-web/core';
import { mapStatusToTextKey, mapStatusToColor } from '../../mapping';

interface InvoiceStatusCardProps {
    data: InvoiceStatusDistribution | null;
    error: Error | null;
    loading: boolean;
    date: string;
}

const createChartDataItem = (statusKey: DisplayBillState, percentage?: number) => ({
    id: statusKey,
    name: <LocalizedText textKey={mapStatusToTextKey[statusKey]} />,
    value: percentage ? percentage / 100 : 0,
    color: mapStatusToColor[statusKey],
});

export const InvoiceStatusCard = ({ data, loading, error, date }: InvoiceStatusCardProps) => {
    const localizeText = useLocalizeText();

    const chartData = [
        createChartDataItem(DisplayBillState.Unpaid, data?.unpaidPercentage),
        createChartDataItem(DisplayBillState.PartiallyPaid, data?.partiallyPaidPercentage),
        createChartDataItem(DisplayBillState.Overdue, data?.overduePercentage),
    ];

    return (
        <FlatChartCard
            data-test-id="invoice-status-card"
            id="invoice-status"
            empty={
                !loading && (!data || chartData.length === 0)
                    ? { message: localizeText({ textKey: TextKey.NoDataAvailable }) }
                    : false
            }
            error={!loading && error ? { message: error.message } : false}
            charts={[
                {
                    data: chartData,
                    id: 'invoice-status-chart',
                    legend: { showValues: true },
                    valueFormatter: (value) => <LocalizedPercentage value={value} />,
                },
            ]}
            title={localizeText({ textKey: TextKey.InvoiceStatus })}
            // @ts-expect-error: this component is not flexible, it expects string but the LocalizedDateFromUtc eventually returns string
            subtitle={
                <Typography data-test-id="date" textStyle="bodySmall" variant="tertiary" as="p">
                    <LocalizedDateFromUtc utcString={date} />
                </Typography>
            }
        />
    );
};
